import React, { useState, useEffect, useRef, useCallback } from 'react';
import './inbox.css';
import toast from 'react-hot-toast';
import {
  getInboxConversations, getInboxMessages, getInboxAgents,
  inboxHandoff, inboxAssign, inboxUnassign, inboxReply, inboxAddNote, inboxReact, inboxResolve, inboxHandback,
  inboxCreateAgent, inboxUpdateAgent, inboxDeleteAgent, inboxFetchMedia,
  inboxClearMessages,
  getInboxCannedResponses, inboxCreateCanned, inboxUpdateCanned, inboxDeleteCanned,
  getInboxLabels, inboxCreateLabel, inboxUpdateLabel, inboxDeleteLabel, inboxSetLabels,
  getInboxContact, getInboxContactHistory, inboxSetContactField,
  searchInboxMessages,
  getInboxReports,
} from '../../../api/client';
import CannedResponseDropdown, { matchCannedTrigger, filterCannedResponses } from '../../../components/common/CannedResponsePicker';
import { parseLabelIds, labelsForConversation, LabelChip, LabelPickerPopover, ColorSwatch } from '../../../components/common/LabelPicker';
import ContactSidebar from '../../../components/common/ContactSidebar';

// ── Utility ───────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)   return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400)return Math.floor(diff / 3600) + 'h ago';
  return Math.floor(diff / 86400) + 'd ago';
}

function modeBadge(mode) {
  const map = {
    bot     : { label: 'Bot',     cls: 'badge-bot'     },
    agent   : { label: 'Agent',   cls: 'badge-agent'   },
    waiting : { label: 'Waiting', cls: 'badge-waiting' },
  };
  const m = map[mode] ?? { label: mode, cls: '' };
  return <span className={`inbox-badge ${m.cls}`}>{m.label}</span>;
}

// Shared confirm dialog -- replaces native window.confirm() everywhere in
// this tab with something that matches the app's own modal styling.
// Usage: setConfirm({ title, message, confirmLabel, danger, onConfirm })
function ConfirmModal({ title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box confirm-modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onCancel}>✕</button>
        </div>
        <div className="modal-body">
          <p className="confirm-modal-msg">{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const AVATAR_PALETTE = ['#7c3aed', '#0ea5a4', '#db2777', '#2563eb', '#ea580c', '#16a34a', '#9333ea', '#0891b2'];
function avatarColor(str = '') {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

// ── Main InboxTab ─────────────────────────────────────────────
export default function InboxTab({ bId }) {
  const [conversations, setConversations]   = useState([]);
  const [selected, setSelected]             = useState(null); // chatId
  const [messages, setMessages]             = useState([]);
  const [agents, setAgents]                 = useState([]);
  const [waitingCount, setWaitingCount]     = useState(0);
  const [loading, setLoading]               = useState(true);
  const [msgLoading, setMsgLoading]         = useState(false);
  const [replyText, setReplyText]           = useState('');
  const [replyingTo, setReplyingTo]         = useState(null); // { waId, text, senderLabel } or null
  const [replyMode, setReplyMode]           = useState('reply'); // 'reply' | 'note'
  const [sending, setSending]               = useState(false);
  const [filterMode, setFilterMode]         = useState('');   // ''=all
  const [labelFilter, setLabelFilter]       = useState(0);    // lId, 0=no filter
  const [convSearch, setConvSearch]         = useState('');
  const [msgSearchResults, setMsgSearchResults] = useState([]);
  const [msgSearchLoading, setMsgSearchLoading] = useState(false);
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);
  const [activeTab, setActiveTab]           = useState('conversations'); // conversations | agents | canned | labels
  const [agentModal, setAgentModal]         = useState(null); // null | 'create' | agent obj
  const [cannedResponses, setCannedResponses] = useState([]);
  const [cannedModal, setCannedModal]       = useState(null); // null | 'create' | canned obj
  const [cannedOpen, setCannedOpen]         = useState(false);   // "/" dropdown visible in reply box
  const [cannedActiveIndex, setCannedActiveIndex] = useState(0);
  const [labels, setLabels]                 = useState([]);
  const [labelModal, setLabelModal]         = useState(null); // null | 'create' | label obj
  const [labelPickerOpen, setLabelPickerOpen] = useState(false); // chat-header "+ Label" popover
  const [reportsRange, setReportsRange]     = useState('week'); // today | week | all
  const [reportsData, setReportsData]       = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [confirm, setConfirm]               = useState(null); // null | { title, message, confirmLabel, danger, onConfirm }
  const [mobileView, setMobileView]         = useState('list'); // list | chat | contact (mobile only)
  const [isMobile, setIsMobile]             = useState(() => window.innerWidth <= 768);
  const [contactOpen, setContactOpen]       = useState(true);   // desktop-only collapse toggle
  const [contact, setContact]               = useState(null);
  const [contactFields, setContactFields]   = useState({});
  const [contactLoading, setContactLoading] = useState(false);
  const [contactEvents, setContactEvents]   = useState([]);
  const [eventsLoading, setEventsLoading]   = useState(false);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [newMsgCount, setNewMsgCount]       = useState(0);       // unseen messages while scrolled up
  const [hasMoreOlder, setHasMoreOlder]     = useState(false);   // more history above what's loaded
  const [loadingOlder, setLoadingOlder]     = useState(false);
  const [convLimit, setConvLimit]           = useState(50);      // "Load more" bumps this up by 50
  const [hasMoreConvs, setHasMoreConvs]     = useState(false);
  const [loadingMoreConvs, setLoadingMoreConvs] = useState(false);
  const messagesEndRef                      = useRef(null);
  const messagesContainerRef                = useRef(null);
  const isNearBottomRef                     = useRef(true);   // tracks if user is scrolled to bottom
  const justSwitchedRef                     = useRef(false);  // true right after picking a new conversation
  const prevMsgCountRef                     = useRef(0);
  const messagesRef                         = useRef([]);     // always-current mirror of `messages`, for the poll/load-older closures below
  const isLoadingOlderRef                   = useRef(false);  // guards against duplicate "load older" triggers while scrolling
  const pollRef                             = useRef(null);

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Current selected conversation object
  const selectedConv = conversations.find(c => c.cChatId === selected) ?? null;

  // Conversations narrowed by the search box
  const filteredConversations = convSearch.trim()
    ? conversations.filter(c => {
        const q = convSearch.trim().toLowerCase();
        return (c.cName || '').toLowerCase().includes(q) || (c.cChatId || '').toLowerCase().includes(q);
      })
    : conversations;

  // ── Full-text message search ─────────────────────────────────
  // convSearch above only ever matches contact name/phone against the
  // conversations already loaded client-side. This debounces a real
  // server-side search across message CONTENT so "did anyone ever say
  // X" works even for a conversation that isn't currently in view.
  useEffect(() => {
    const q = convSearch.trim();
    if (q.length < 2) { setMsgSearchResults([]); setMsgSearchLoading(false); return; }
    setMsgSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchInboxMessages(bId, q);
        setMsgSearchResults(res.data?.results ?? []);
      } catch { setMsgSearchResults([]); }
      setMsgSearchLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [convSearch, bId]);

  // ── Load conversations ──────────────────────────────────────
  //  Uses convLimit (bumped by 50 each time "Load more" is clicked)
  //  rather than true offset pagination — simplest way to keep the
  //  5s silent poll's full-replace behaviour correct without having
  //  to merge/de-dupe pages by hand.
  const loadConversations = useCallback(async (silent = false, limitOverride = null) => {
    if (!silent) setLoading(true);
    try {
      const res = await getInboxConversations(bId, {
        mode: filterMode, status: 'open', limit: limitOverride ?? convLimit,
        ...(labelFilter ? { label: labelFilter } : {}),
      });
      const d = res.data;
      setConversations(d.conversations ?? []);
      setWaitingCount(d.waiting_count ?? 0);
      setHasMoreConvs(!!d.has_more);
    } catch { if (!silent) toast.error('Failed to load conversations'); }
    if (!silent) setLoading(false);
  }, [bId, filterMode, labelFilter, convLimit]);

  async function loadMoreConversations() {
    setLoadingMoreConvs(true);
    const nextLimit = convLimit + 50;
    await loadConversations(true, nextLimit);
    setConvLimit(nextLimit);
    setLoadingMoreConvs(false);
  }

  // Switching filters starts back at the first page
  useEffect(() => { setConvLimit(50); }, [filterMode, labelFilter]);

  // ── Load messages for selected conversation (initial load / full reset) ──
  //  Always anchors to the NEWEST end of the conversation — see the
  //  backend fix notes in api/bots/inbox/index.php for why that matters.
  const loadMessages = useCallback(async (chatId, silent = false, aroundId = null) => {
    if (!silent) setMsgLoading(true);
    try {
      const res = await getInboxMessages(bId, chatId, aroundId ? { around: aroundId, limit: 50 } : { limit: 50 });
      setMessages(res.data?.messages ?? []);
      setHasMoreOlder(!!res.data?.has_more);
    } catch { /* silent */ }
    if (!silent) setMsgLoading(false);
  }, [bId]);

  // ── Load older messages — triggered by scrolling near the top,
  //    or by the "Load earlier messages" button. Prepends to the
  //    array and restores scroll position so the view doesn't jump. ──
  const loadOlderMessages = useCallback(async (chatId) => {
    const current = messagesRef.current;
    if (isLoadingOlderRef.current || current.length === 0) return;
    const oldestId = current[0]?.mId;
    if (oldestId == null) return;

    isLoadingOlderRef.current = true;
    setLoadingOlder(true);
    const el = messagesContainerRef.current;
    const prevScrollHeight = el ? el.scrollHeight : 0;

    try {
      const res = await getInboxMessages(bId, chatId, { before: oldestId, limit: 50 });
      const older = res.data?.messages ?? [];
      setHasMoreOlder(!!res.data?.has_more);
      if (older.length > 0) {
        setMessages(prev => [...older, ...prev]);
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
        });
      }
    } catch { /* silent */ }
    setLoadingOlder(false);
    isLoadingOlderRef.current = false;
  }, [bId]);

  // ── Poll: fetch only what's new since the last message we have,
  //    and append rather than replace — so a long conversation never
  //    silently caps out, and any older history loaded via "load
  //    older" above stays put instead of getting wiped every 5s. ──
  const pollNewMessages = useCallback(async (chatId) => {
    const current = messagesRef.current;
    const newestId = current.length ? current[current.length - 1].mId : null;
    if (newestId == null) return;
    try {
      const res = await getInboxMessages(bId, chatId, { after: newestId });
      const fresh = res.data?.messages ?? [];
      if (fresh.length > 0) setMessages(prev => [...prev, ...fresh]);
    } catch { /* silent */ }
  }, [bId]);

  // ── Load agents ─────────────────────────────────────────────
  const loadAgents = useCallback(async () => {
    try {
      const res = await getInboxAgents(bId);
      setAgents(res.data?.agents ?? []);
    } catch { /* silent */ }
  }, [bId]);

  // ── Load canned responses ───────────────────────────────────
  const loadCannedResponses = useCallback(async () => {
    try {
      const res = await getInboxCannedResponses(bId);
      setCannedResponses(res.data?.canned_responses ?? []);
    } catch { /* silent */ }
  }, [bId]);

  // ── Load labels ──────────────────────────────────────────────
  const loadLabels = useCallback(async () => {
    try {
      const res = await getInboxLabels(bId);
      setLabels(res.data?.labels ?? []);
    } catch { /* silent */ }
  }, [bId]);

  const labelsById = labels.reduce((acc, l) => { acc[l.lId] = l; return acc; }, {});

  const loadReports = useCallback(async (range = reportsRange) => {
    setReportsLoading(true);
    try {
      const res = await getInboxReports(bId, range);
      setReportsData(res.data ?? null);
    } catch { toast.error('Failed to load reports'); }
    setReportsLoading(false);
  }, [bId, reportsRange]);

  // ── Initial load ────────────────────────────────────────────
  useEffect(() => {
    loadConversations();
    loadAgents();
    loadCannedResponses();
    loadLabels();
  }, [loadConversations, loadAgents, loadCannedResponses, loadLabels]);

  // ── Poll every 5s ───────────────────────────────────────────
  useEffect(() => {
    pollRef.current = setInterval(() => {
      loadConversations(true);
      if (selected) pollNewMessages(selected);
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [loadConversations, pollNewMessages, selected]);

  // ── Scroll behaviour, WhatsApp-style ───────────────────────────
  // - If you're at the bottom, new messages scroll you down automatically.
  // - If you've scrolled up to read history, we leave you alone and
  //   just count what came in, surfaced via a "↓ N new messages" pill.
  useEffect(() => {
    if (messages.length === 0) return; // nothing to scroll to yet
    const added = messages.length - prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;

    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: justSwitchedRef.current ? 'auto' : 'smooth' });
      justSwitchedRef.current = false;
      setNewMsgCount(0);
    } else if (added > 0) {
      setNewMsgCount(c => c + added);
    }
  }, [messages]);

  // ── Track whether user is scrolled near the bottom, and auto-load
  //    older history once they scroll near the top (mirrors how
  //    WhatsApp Web itself lazy-loads further back as you scroll up,
  //    so a long conversation never just "runs out" of visible history) ──
  function handleMessagesScroll() {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nowNearBottom = distanceFromBottom < 80;
    isNearBottomRef.current = nowNearBottom;
    if (nowNearBottom) setNewMsgCount(0);

    if (el.scrollTop < 100 && hasMoreOlder && !isLoadingOlderRef.current && selected) {
      loadOlderMessages(selected);
    }
  }

  function jumpToBottom() {
    isNearBottomRef.current = true;
    setNewMsgCount(0);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  // ── Select conversation ─────────────────────────────────────
  async function selectConversation(chatId) {
    setSelected(chatId);
    setMessages([]);
    setHasMoreOlder(false);
    isNearBottomRef.current = true;   // always land on the bottom for a freshly opened chat
    justSwitchedRef.current = true;
    prevMsgCountRef.current = 0;
    setNewMsgCount(0);
    setMobileView('chat');            // on mobile, switch to the chat view
    setLabelPickerOpen(false);
    await loadMessages(chatId);
    loadContact(chatId);
  }

  // ── Jump to a specific message from a full-text search result ───
  async function jumpToMessage(chatId, mId) {
    setSelected(chatId);
    setMessages([]);
    setHasMoreOlder(false);
    isNearBottomRef.current = false;  // we're jumping to a specific spot, not the bottom
    justSwitchedRef.current = true;
    prevMsgCountRef.current = 0;
    setNewMsgCount(0);
    setMobileView('chat');
    setLabelPickerOpen(false);
    setConvSearch('');
    setMsgSearchResults([]);
    await loadMessages(chatId, false, mId);
    loadContact(chatId);
    setHighlightedMsgId(mId);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = messagesContainerRef.current?.querySelector(`[data-msg-id="${mId}"]`);
        if (el) el.scrollIntoView({ block: 'center' });
      }, 60);
    });
    setTimeout(() => setHighlightedMsgId(null), 2200);
  }

  // ── Contact Profile Sidebar data ─────────────────────────────
  async function loadContact(chatId) {
    setContactLoading(true);
    setEventsLoading(true);
    try {
      const res = await getInboxContact(bId, chatId);
      setContact(res.data?.contact ?? null);
      setContactFields(res.data?.fields ?? {});
    } catch { setContact(null); setContactFields({}); }
    setContactLoading(false);

    try {
      const res = await getInboxContactHistory(bId, chatId);
      setContactEvents(res.data?.events ?? []);
    } catch { setContactEvents([]); }
    setEventsLoading(false);
  }

  async function handleSaveContactField(key, value) {
    if (!selected) return;
    try {
      await inboxSetContactField(bId, selected, key, value);
      setContactFields(f => ({ ...f, [key]: value }));
    } catch { toast.error('Failed to save'); }
  }

  function closeChat() {
    setSelected(null);
    setMessages([]);
    setMobileView('list');
    setNewMsgCount(0);
  }

  // ── Escape closes the open chat (or modal, or clears a draft) ─
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key !== 'Escape') return;
      if (agentModal || cannedModal || labelModal) { setAgentModal(null); setCannedModal(null); setLabelModal(null); return; }
      if (labelPickerOpen) { setLabelPickerOpen(false); return; }
      if (cannedOpen) { setCannedOpen(false); return; }
      if (replyText.trim()) { setReplyText(''); return; }
      if (selected) closeChat();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [agentModal, cannedModal, labelModal, labelPickerOpen, cannedOpen, replyText, selected]);

  // ── Reply ───────────────────────────────────────────────────
  async function handleReply(e) {
    e.preventDefault();
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      if (replyMode === 'note') {
        await inboxAddNote(bId, selected, selectedConv?.cAgentId ?? null, replyText.trim());
      } else {
        await inboxReply(bId, selected, selectedConv?.cAgentId ?? null, replyText.trim(), replyingTo?.waId ?? null);
      }
      setReplyText('');
      setReplyingTo(null);
      isNearBottomRef.current = true; // always show your own reply/note
      await pollNewMessages(selected);
      await loadConversations(true);
    } catch { toast.error(replyMode === 'note' ? 'Failed to add note' : 'Failed to send reply'); }
    setSending(false);
  }

  async function handleReact(msg, emoji) {
    if (!msg.mWaMessageId || !selected) {
      toast.error("Can't react — this message has no WhatsApp ID on record");
      return;
    }
    try {
      await inboxReact(bId, selected, msg.mWaMessageId, emoji);
      isNearBottomRef.current = true;
      await pollNewMessages(selected);
    } catch { toast.error('Failed to send reaction'); }
  }

  // ── Toggle a label on/off the currently selected conversation ───
  // Optimistically updates local state so the chip appears/disappears
  // instantly, then reconciles with the server response.
  async function toggleConversationLabel(lId) {
    if (!selected || !selectedConv) return;
    const current = parseLabelIds(selectedConv.cLabels);
    const next = current.includes(lId) ? current.filter(id => id !== lId) : [...current, lId];
    setConversations(prev => prev.map(c =>
      c.cChatId === selected ? { ...c, cLabels: next.join(',') } : c
    ));
    try {
      await inboxSetLabels(bId, selected, next);
    } catch {
      toast.error('Failed to update labels');
      loadConversations(true); // revert to server truth on failure
    }
  }

  // ── Assign ──────────────────────────────────────────────────
  async function doAssign(agentId) {
    try {
      await inboxAssign(bId, selected, agentId);
      toast.success('Assigned');
      await loadConversations(true);
    } catch { toast.error('Failed to assign'); }
  }
  function handleAssign(agentId) {
    const agent = agents.find(a => String(a.aId) === String(agentId));
    const isReassign = !!selectedConv?.cAgentId;
    setConfirm({
      title: isReassign ? 'Reassign this conversation?' : 'Assign this conversation?',
      message: isReassign
        ? `This conversation is currently assigned to someone else. Reassign it to ${agent?.aName ?? 'this agent'}?`
        : `Assign this conversation to ${agent?.aName ?? 'this agent'}? They'll be notified via WhatsApp and can reply from their agent console.`,
      confirmLabel: isReassign ? 'Reassign' : 'Assign',
      onConfirm: () => { doAssign(agentId); setConfirm(null); },
    });
  }

  // ── Unassign ────────────────────────────────────────────────
  async function doUnassign() {
    try {
      await inboxUnassign(bId, selected);
      toast.success('Agent removed');
      await loadConversations(true);
    } catch { toast.error('Failed to remove agent'); }
  }
  function handleUnassign() {
    setConfirm({
      title: 'Remove assigned agent?',
      message: 'This conversation will go back to the waiting queue for another agent to pick up.',
      confirmLabel: 'Remove agent',
      danger: true,
      onConfirm: () => { doUnassign(); setConfirm(null); },
    });
  }

  // ── Handback to bot ─────────────────────────────────────────
  async function doHandback() {
    try {
      await inboxHandback(bId, selected);
      toast.success('Handed back to bot');
      await loadConversations(true);
      await loadMessages(selected, true);
    } catch { toast.error('Failed'); }
  }
  function handleHandback() {
    setConfirm({
      title: 'Hand back to bot?',
      message: 'The customer will be returned to the automated bot flow, and this conversation will no longer be assigned to an agent.',
      confirmLabel: 'Hand to Bot',
      onConfirm: () => { doHandback(); setConfirm(null); },
    });
  }

  // ── Resolve ─────────────────────────────────────────────────
  async function doResolve() {
    try {
      await inboxResolve(bId, selected);
      toast.success('Resolved');
      closeChat();
      await loadConversations();
    } catch { toast.error('Failed'); }
  }
  function handleResolve() {
    setConfirm({
      title: 'Mark as resolved?',
      message: 'This conversation will be closed and moved out of your open queue.',
      confirmLabel: 'Resolve',
      onConfirm: () => { doResolve(); setConfirm(null); },
    });
  }

  // ── Clear messages (your CmdKit inbox only — never touches WhatsApp) ──
  async function doClearMessages() {
    try {
      await inboxClearMessages(bId, selected);
      toast.success('Chat cleared on your end');
      setMessages([]);
      setHasMoreOlder(false);
      await loadConversations(true);
    } catch { toast.error('Failed to clear messages'); }
  }
  function handleClearMessages() {
    setConfirm({
      title: 'Clear this chat?',
      message: "This only clears the message history here on CmdKit — it never touches WhatsApp itself. " +
        "The customer keeps their own copy of every message on their phone, and they won't see or feel " +
        "anything happen. New messages will keep coming in normally right after this.",
      confirmLabel: 'Clear chat',
      danger: true,
      onConfirm: () => { doClearMessages(); setConfirm(null); },
    });
  }

  // ── Canned response "/" autocomplete ─────────────────────────
  const cannedQuery = matchCannedTrigger(replyText);
  const cannedMatches = cannedQuery !== null ? filterCannedResponses(cannedResponses, cannedQuery) : [];

  function handleReplyTextChange(val) {
    setReplyText(val);
    const q = matchCannedTrigger(val);
    if (replyMode === 'reply' && q !== null && cannedResponses.length > 0) {
      setCannedOpen(true);
      setCannedActiveIndex(0);
    } else {
      setCannedOpen(false);
    }
  }

  function pickCanned(c) {
    setReplyText(c.crBody);
    setCannedOpen(false);
  }

  function handleReplyKeyDown(e) {
    if (cannedOpen && cannedMatches.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCannedActiveIndex(i => (i + 1) % cannedMatches.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setCannedActiveIndex(i => (i - 1 + cannedMatches.length) % cannedMatches.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickCanned(cannedMatches[cannedActiveIndex]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setCannedOpen(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleReply(e);
    }
  }

  // ── Render ──────────────────────────────────────────────────
  return (
    <div
      className={`inbox-wrap mobile-view-${mobileView}`}
      style={{ display: 'flex', flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}
    >

      {/* ── Sidebar ────────────────────────────────────────── */}
      <aside
        className="inbox-sidebar"
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}
      >
        <div className="inbox-sidebar-header">
          <div className="inbox-header-row">
            <div>
              <div className="inbox-header-title">Inbox</div>
              <div className="inbox-header-sub">
                {conversations.length} conversation{conversations.length === 1 ? '' : 's'}
                {waitingCount > 0 ? ` · ${waitingCount} waiting` : ''}
              </div>
            </div>
            <button
              type="button"
              className={`inbox-refresh-btn ${loading ? 'is-loading' : ''}`}
              onClick={() => loadConversations()}
              title="Refresh"
              aria-label="Refresh"
            >
              ⟳
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            gap: 4,
            background: '#f3f4f6',
            border: '1.5px solid #e5e7eb',
            borderRadius: 10,
            padding: 3,
            boxSizing: 'border-box',
          }}>
            {[
              { key: 'conversations', label: 'Chats',  badge: waitingCount, onClick: () => setActiveTab('conversations') },
              { key: 'agents',        label: 'Agents', onClick: () => { setActiveTab('agents'); loadAgents(); } },
              { key: 'canned',        label: 'Canned', onClick: () => { setActiveTab('canned'); loadCannedResponses(); } },
              { key: 'labels',        label: 'Labels', onClick: () => { setActiveTab('labels'); loadLabels(); } },
              { key: 'reports',       label: 'Reports', onClick: () => { setActiveTab('reports'); loadReports(); } },
            ].map(tab => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={tab.onClick}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    minWidth: 0,
                    padding: '7px 4px',
                    borderRadius: 7,
                    border: 'none',
                    background: active ? '#fff' : 'transparent',
                    color: active ? '#00966b' : '#6b7280',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                    fontSize: 11.5,
                    fontWeight: 700,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                  }}
                >
                  {/* Truncation lives on this inner span, not the flex
                      button itself — a centered flex container clips
                      overflowing text symmetrically on BOTH ends with no
                      ellipsis (that's what produced "onversatio" from
                      "Conversations"). A block-level span with its own
                      overflow/text-overflow correctly truncates from the
                      end with "…" if a label is ever too long. */}
                  <span style={{
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {tab.label}
                  </span>
                  {!!tab.badge && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 15,
                      height: 15,
                      padding: '0 4px',
                      borderRadius: 999,
                      background: '#ef4444',
                      color: '#fff',
                      fontSize: 9.5,
                      fontWeight: 800,
                      boxSizing: 'border-box',
                      flexShrink: 0,
                    }}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === 'conversations' && (
          <>
            {/* Search */}
            <div className="search-bar inbox-search-bar">
              <span className="search-icon">🔍</span>
              <input
                className="search-input"
                placeholder="Search by name, phone, or message…"
                value={convSearch}
                onChange={e => setConvSearch(e.target.value)}
              />
            </div>

            {/* Full-text message search results — only when the query
                doesn't just match a contact's name/phone, i.e. it's
                actually searching message CONTENT */}
            {convSearch.trim().length >= 2 && (
              <div style={{ borderBottom: '1.5px solid #e5e7eb', flexShrink: 0 }}>
                <div style={{ padding: '8px 14px 4px', fontSize: 10.5, fontWeight: 700, color: '#8a94a3', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  Message matches {msgSearchLoading && '…'}
                </div>
                {!msgSearchLoading && msgSearchResults.length === 0 ? (
                  <div style={{ padding: '2px 14px 12px', fontSize: 12, color: '#9ca3af' }}>
                    No messages contain "{convSearch.trim()}"
                  </div>
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 6px', maxHeight: 220, overflowY: 'auto' }}>
                    {msgSearchResults.map(r => (
                      <li key={r.chatId}>
                        {r.matches.map(m => (
                          <button
                            key={m.mId}
                            type="button"
                            onClick={() => jumpToMessage(r.chatId, m.mId)}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left',
                              padding: '7px 14px', border: 'none', background: 'transparent',
                              cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                              WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>
                              {r.cName || r.chatId}
                              {r.matchCount > 1 && (
                                <span style={{ fontWeight: 600, color: '#9ca3af', fontSize: 10.5 }}> · {r.matchCount} matches</span>
                              )}
                            </div>
                            <div style={{
                              fontSize: 11.5, color: '#6b7280', marginTop: 1,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {m.mText}
                            </div>
                          </button>
                        ))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Filter pills */}
            <div className="inbox-filter-row">
              {['', 'waiting', 'agent', 'bot'].map(m => (
                <button
                  key={m}
                  className={`inbox-filter-pill ${filterMode === m ? 'active' : ''}`}
                  onClick={() => setFilterMode(m)}
                >
                  {m === '' ? 'All' : m.charAt(0).toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>

            {/* Label filter chips — only shown once at least one label exists */}
            {labels.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, margin: '0 10px 10px', flexShrink: 0 }}>
                {labels.map(l => {
                  const active = labelFilter === l.lId;
                  return (
                    <button
                      key={l.lId}
                      type="button"
                      onClick={() => setLabelFilter(f => f === l.lId ? 0 : l.lId)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 999,
                        border: `1.5px solid ${active ? l.lColor : `${l.lColor}66`}`,
                        background: active ? l.lColor : 'transparent',
                        color: active ? '#fff' : l.lColor,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                      }}
                    >
                      {l.lName}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Conversation list */}
            {loading ? (
              <div className="inbox-loading">Loading…</div>
            ) : filteredConversations.length === 0 ? (
              <div className="inbox-empty">
                {convSearch.trim() ? 'No matches' : 'No conversations yet'}
              </div>
            ) : (
              <>
                <ul className="inbox-conv-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', listStyle: 'none', margin: 0, padding: 0 }}>
                  {filteredConversations.map(conv => {
                    const convLabels = labelsForConversation(conv, labelsById);
                    return (
                      <li
                        key={conv.cChatId}
                        className={`inbox-conv-item ${selected === conv.cChatId ? 'active' : ''} ${conv.cMode === 'waiting' ? 'is-waiting' : ''}`}
                        onClick={() => selectConversation(conv.cChatId)}
                      >
                        <div className="conv-avatar" style={{ background: avatarColor(conv.cName || conv.cChatId) }}>
                          {(conv.cName || conv.cChatId)[0].toUpperCase()}
                        </div>
                        <div className="conv-meta">
                          <div className="conv-top">
                            <span className="conv-name">{conv.cName || conv.cChatId}</span>
                            <span className="conv-time">{timeAgo(conv.cLastMsgAt)}</span>
                          </div>
                          <div className="conv-bottom">
                            <span className="conv-preview">{conv.cLastMsg || '—'}</span>
                            {modeBadge(conv.cMode)}
                          </div>
                          {convLabels.length > 0 && (
                            <div className="conv-labels">
                              {convLabels.map(l => <LabelChip key={l.lId} label={l} small />)}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {hasMoreConvs && !convSearch.trim() && (
                  <button
                    type="button"
                    className="inbox-load-more-convs-btn"
                    onClick={loadMoreConversations}
                    disabled={loadingMoreConvs}
                  >
                    {loadingMoreConvs ? 'Loading…' : 'Load more conversations'}
                  </button>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'agents' && (
          <AgentsPanel
            bId={bId}
            agents={agents}
            onReload={loadAgents}
            agentModal={agentModal}
            setAgentModal={setAgentModal}
          />
        )}

        {activeTab === 'canned' && (
          <CannedResponsesPanel
            bId={bId}
            cannedResponses={cannedResponses}
            onReload={loadCannedResponses}
            cannedModal={cannedModal}
            setCannedModal={setCannedModal}
          />
        )}

        {activeTab === 'labels' && (
          <LabelsPanel
            bId={bId}
            labels={labels}
            onReload={loadLabels}
            labelModal={labelModal}
            setLabelModal={setLabelModal}
          />
        )}

        {activeTab === 'reports' && (
          <ReportsPanel
            data={reportsData}
            loading={reportsLoading}
            range={reportsRange}
            onRangeChange={r => { setReportsRange(r); loadReports(r); }}
          />
        )}
      </aside>

      {/* ── Chat panel ─────────────────────────────────────── */}
      <main
        className="inbox-chat"
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}
      >
        {!selected ? (
          <div className="inbox-placeholder">
            <span>👈 Select a conversation to start</span>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div style={{ flexShrink: 0, borderBottom: '1.5px solid #e5e7eb', background: '#fff', boxSizing: 'border-box' }}>
              {/* Identity row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
                <button
                  type="button"
                  className="inbox-back-btn"
                  onClick={() => setMobileView('list')}
                  aria-label="Back to conversations"
                >
                  ←
                </button>
                <div className="conv-avatar lg" style={{ flexShrink: 0 }}>
                  {(selectedConv?.cName || selected)[0]?.toUpperCase()}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedConv?.cName || selected}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#8a94a3' }}>{selected}</div>
                </div>
                {selectedConv && modeBadge(selectedConv.cMode)}
                <button
                  type="button"
                  onClick={() => isMobile ? setMobileView('contact') : setContactOpen(o => !o)}
                  title="Contact details"
                  aria-label="Toggle contact details"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, minWidth: 28, borderRadius: '50%',
                    border: `1.5px solid ${contactOpen && !isMobile ? '#00C88C' : '#e5e7eb'}`,
                    background: contactOpen && !isMobile ? '#e8f9f3' : '#fff',
                    color: contactOpen && !isMobile ? '#00966b' : '#6b7280',
                    fontSize: 12, cursor: 'pointer', flexShrink: 0, boxSizing: 'border-box',
                    WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                  }}
                >
                  ⓘ
                </button>
                <button
                  type="button"
                  onClick={closeChat}
                  title="Close chat (Esc)"
                  aria-label="Close chat"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, minWidth: 28, borderRadius: '50%',
                    border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280',
                    fontSize: 12, cursor: 'pointer', flexShrink: 0, boxSizing: 'border-box',
                    WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Toolbar row — labels + assignment + lifecycle actions, grouped
                  together with a subtle tint so it reads as one coherent bar
                  instead of scattered controls */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                padding: '9px 14px',
                background: '#f9fafb',
                borderTop: '1px solid #f0f1f3',
                boxSizing: 'border-box',
              }}>
                {labelsForConversation(selectedConv, labelsById).map(l => (
                  <LabelChip key={l.lId} label={l} onRemove={() => toggleConversationLabel(l.lId)} />
                ))}
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setLabelPickerOpen(o => !o)}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 999,
                      border: '1.5px dashed #cbd5e1',
                      background: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none',
                    }}
                  >
                    + Label
                  </button>
                  {labelPickerOpen && (
                    <LabelPickerPopover
                      labels={labels}
                      selectedIds={parseLabelIds(selectedConv?.cLabels)}
                      onToggle={toggleConversationLabel}
                      onClose={() => setLabelPickerOpen(false)}
                    />
                  )}
                </div>

                {/* divider between "metadata" controls and "lifecycle" actions */}
                <div style={{ width: 1, alignSelf: 'stretch', minHeight: 20, background: '#e5e7eb', margin: '0 2px' }} />

                {agents.length > 0 && (
                  <select
                    value={selectedConv?.cAgentId ?? ''}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === '__unassign__') handleUnassign();
                      else if (v) handleAssign(v);
                    }}
                    style={{
                      padding: '5px 8px',
                      borderRadius: 8,
                      border: '1.5px solid #e5e7eb',
                      background: '#fff',
                      color: '#374151',
                      fontSize: 11.5,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                      maxWidth: 150,
                    }}
                  >
                    <option value="">Assign agent…</option>
                    {agents.map(a => (
                      <option key={a.aId} value={a.aId}>{a.aName}</option>
                    ))}
                    {selectedConv?.cAgentId && (
                      <option value="__unassign__">— Remove assigned agent —</option>
                    )}
                  </select>
                )}

                <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                  <button
                    type="button"
                    onClick={handleHandback}
                    title="Hand back to the bot"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                      background: '#fff', color: '#374151', fontSize: 11.5, fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                      WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                    }}
                  >
                    ↩ <span className="btn-label">Hand to Bot</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleClearMessages}
                    title="Clear chat history (your CmdKit side only)"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                      background: '#fff', color: '#374151', fontSize: 11.5, fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                      WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                    }}
                  >
                    🧹 <span className="btn-label">Clear</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleResolve}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 12px', borderRadius: 8, border: 'none',
                      background: '#00C88C', color: '#fff', fontSize: 11.5, fontWeight: 700,
                      cursor: 'pointer', fontFamily: 'inherit', boxSizing: 'border-box',
                      WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                    }}
                  >
                    ✓ <span className="btn-label">Resolve</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div
              className="inbox-messages"
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
              style={{ flex: 1, minHeight: 0, overflowY: 'auto', boxSizing: 'border-box' }}
            >
              {loadingOlder && (
                <div className="inbox-loading-older">Loading earlier messages…</div>
              )}
              {!msgLoading && !loadingOlder && hasMoreOlder && messages.length > 0 && (
                <button
                  type="button"
                  className="inbox-load-older-btn"
                  onClick={() => loadOlderMessages(selected)}
                >
                  ↑ Load earlier messages
                </button>
              )}
              {msgLoading ? (
                <div className="inbox-loading">Loading messages…</div>
              ) : messages.length === 0 ? (
                <div className="inbox-empty">No messages yet</div>
              ) : (
                messages.map(m => (
                  <MessageBubble
                    key={m.mId}
                    msg={m}
                    bId={bId}
                    highlighted={m.mId === highlightedMsgId}
                    onReply={() => { setReplyMode('reply'); setReplyingTo({
                      waId: m.mWaMessageId,
                      text: m.mText || (m.mType ? `[${m.mType}]` : ''),
                      senderLabel: m.mDirection === 'out' ? (m.agentName || 'You') : (selectedConv?.cName || 'Customer'),
                    }); }}
                    onReact={emoji => handleReact(m, emoji)}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {newMsgCount > 0 && (
              <button type="button" className="inbox-jump-btn" onClick={jumpToBottom}>
                ↓ {newMsgCount} new message{newMsgCount > 1 ? 's' : ''}
              </button>
            )}

            {/* Composer — toggle + reply/note box, always pinned to the bottom */}
            <div style={{ flexShrink: 0, borderTop: '1.5px solid #e5e7eb', background: '#fff', boxSizing: 'border-box' }}>
              {/* Quoted reply preview */}
              {replyingTo && replyMode === 'reply' && (
                <div className="inbox-reply-preview">
                  <div className="inbox-reply-preview-bar" />
                  <div className="inbox-reply-preview-body">
                    <div className="inbox-reply-preview-label">Replying to {replyingTo.senderLabel}</div>
                    <div className="inbox-reply-preview-text">{replyingTo.text}</div>
                  </div>
                  <button type="button" className="inbox-reply-preview-close" onClick={() => setReplyingTo(null)}>✕</button>
                </div>
              )}

              {/* Reply / Note mode toggle */}
              <div style={{ display: 'flex', gap: 6, padding: '10px 14px 0', boxSizing: 'border-box' }}>
                <button
                  type="button"
                  onClick={() => setReplyMode('reply')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: replyMode === 'reply' ? '#e8f9f3' : 'transparent',
                    color: replyMode === 'reply' ? '#00966b' : '#8a94a3',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                  }}
                >
                  <span style={{ fontSize: 13 }}>↩</span> Reply
                </button>
                <button
                  type="button"
                  onClick={() => { setReplyMode('note'); setReplyingTo(null); setCannedOpen(false); }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: replyMode === 'note' ? '#fef3c7' : 'transparent',
                    color: replyMode === 'note' ? '#92400e' : '#8a94a3',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                  }}
                >
                  <span style={{ fontSize: 13 }}>📝</span> Note
                </button>
              </div>

              {/* Reply box */}
              <form
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 10,
                  padding: '10px 14px 14px',
                  boxSizing: 'border-box',
                  background: replyMode === 'note' ? '#fffbeb' : 'transparent',
                }}
                onSubmit={handleReply}
              >
                {cannedOpen && replyMode === 'reply' && (
                  <CannedResponseDropdown
                    items={cannedMatches}
                    activeIndex={cannedActiveIndex}
                    onPick={pickCanned}
                    onHoverIndex={setCannedActiveIndex}
                  />
                )}
                <textarea
                  style={{
                    flex: 1,
                    minWidth: 0,
                    resize: 'none',
                    border: `1.5px solid ${replyMode === 'note' ? '#fcd34d' : '#e5e7eb'}`,
                    borderRadius: 12,
                    padding: '10px 14px',
                    fontSize: 13.5,
                    fontFamily: 'inherit',
                    lineHeight: 1.4,
                    color: '#1f2937',
                    background: '#fff',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                  placeholder={replyMode === 'note'
                    ? 'Leave a private note — only your team can see this…'
                    : 'Type a reply… (type / for saved replies)'}
                  value={replyText}
                  rows={2}
                  onChange={e => handleReplyTextChange(e.target.value)}
                  onKeyDown={handleReplyKeyDown}
                />
                <button
                  type="submit"
                  aria-label={replyMode === 'note' ? 'Add note' : 'Send message'}
                  title={replyMode === 'note' ? 'Add note' : 'Send message'}
                  disabled={sending || !replyText.trim()}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    borderRadius: '50%',
                    border: 'none',
                    background: !replyText.trim() ? '#d1d5db' : (replyMode === 'note' ? '#d97706' : '#00C88C'),
                    color: '#fff',
                    fontSize: 16,
                    cursor: (sending || !replyText.trim()) ? 'default' : 'pointer',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    transition: 'background 0.12s',
                  }}
                >
                  {sending ? (
                    <span style={{
                      width: 14, height: 14, borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.5)', borderTopColor: '#fff',
                      animation: 'inbox-spin 0.7s linear infinite',
                    }} />
                  ) : replyMode === 'note' ? (
                    <i className="fas fa-note-sticky" />
                  ) : (
                    <i className="fas fa-paper-plane" style={{ marginLeft: -1 }} />
                  )}
                </button>
              </form>
            </div>
          </>
        )}
      </main>

      {/* ── Contact Profile Sidebar (3rd column) ──────────────── */}
      {selected && (isMobile ? mobileView === 'contact' : contactOpen) && (
        <ContactSidebar
          loading={contactLoading}
          contact={contact}
          fields={contactFields}
          events={contactEvents}
          eventsLoading={eventsLoading}
          chatId={selected}
          convName={selectedConv?.cName}
          convLabels={selectedConv?.cLabels}
          allLabels={labels}
          onToggleLabel={toggleConversationLabel}
          onSaveField={handleSaveContactField}
          onClose={isMobile ? () => setMobileView('chat') : () => setContactOpen(false)}
          fullWidth={isMobile}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// ── Message bubble ────────────────────────────────────────────
function MessageBubble({ msg, bId, onReply, onReact, highlighted }) {
  const isOut   = msg.mDirection === 'out';
  const isAgent = msg.mSenderType === 'agent';
  const isNote  = msg.mSenderType === 'note';

  // Wider palette here vs. the desktop quick-react — right-click already
  // implies "let me choose", so give a real set to choose from.
  const REACT_EMOJIS = ['👍','❤️','😂','😮','😢','🙏','🔥','🎉','👏','😍','🤔','😅','😎','💯','✅','❌','😡','🥳','👀','🙌'];

  // ctxMenu: { mode: 'menu' | 'emoji', x, y } — mode 'menu' shows Reply/React,
  // mode 'emoji' shows the full picker. x/y are viewport coordinates (used
  // with position:fixed) so this can never drift relative to a scrolled
  // ancestor the way the old bubble-anchored version could.
  const [ctxMenu, setCtxMenu] = useState(null);

  // Swipe-to-reply (touch only) — horizontal drag distance, purely for
  // the visual slide-and-reveal feedback while dragging.
  const [swipeX, setSwipeX] = useState(0);
  const touch = useRef({ startX: 0, startY: 0, swiping: false, longPressTimer: null });

  const canAct = !!msg.mWaMessageId && (onReply || onReact);

  function openMenuAt(x, y, mode = 'menu') {
    const clampedX = Math.min(Math.max(x, 8), window.innerWidth - 8);
    const clampedY = Math.min(Math.max(y, 8), window.innerHeight - 8);
    setCtxMenu({ mode, x: clampedX, y: clampedY });
  }

  function handleContextMenu(e) {
    if (!canAct) return;
    e.preventDefault();
    openMenuAt(e.clientX, e.clientY);
  }

  function handleTouchStart(e) {
    if (!canAct) return;
    const t = e.touches[0];
    touch.current.startX = t.clientX;
    touch.current.startY = t.clientY;
    touch.current.swiping = false;
    // On touch, swipe already covers Reply — a long-press goes straight to
    // the reaction picker rather than showing an intermediate Reply/React
    // menu, since there's nothing left to disambiguate.
    touch.current.longPressTimer = onReact ? setTimeout(() => {
      if (touch.current.swiping) return; // moved too much — treat as a swipe, not a hold
      if (navigator.vibrate) navigator.vibrate(12);
      openMenuAt(t.clientX, t.clientY, 'emoji');
    }, 450) : null;
  }

  function handleTouchMove(e) {
    if (!canAct) return;
    const t = e.touches[0];
    const dx = t.clientX - touch.current.startX;
    const dy = t.clientY - touch.current.startY;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      if (touch.current.longPressTimer) { clearTimeout(touch.current.longPressTimer); touch.current.longPressTimer = null; }
    }
    // Predominantly-rightward drag = swipe-to-reply. Left swipe and
    // vertical scroll are both ignored so the message list still scrolls.
    if (dx > 8 && Math.abs(dx) > Math.abs(dy)) {
      touch.current.swiping = true;
      setSwipeX(Math.min(dx, 70));
    }
  }

  function handleTouchEnd() {
    if (touch.current.longPressTimer) { clearTimeout(touch.current.longPressTimer); touch.current.longPressTimer = null; }
    if (touch.current.swiping && swipeX > 50 && onReply) onReply();
    touch.current.swiping = false;
    setSwipeX(0);
  }

  // Close the menu on outside click/tap, scroll, or Escape.
  useEffect(() => {
    if (!ctxMenu) return;
    function close() { setCtxMenu(null); }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    document.addEventListener('scroll', close, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      document.removeEventListener('scroll', close, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu]);

  // Use already-downloaded URL if available, otherwise fetch it
  const [imgSrc, setImgSrc]     = useState(msg.mMediaUrl || null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    // If we already have a URL, use it directly — no API call needed
    if (msg.mMediaUrl) { setImgSrc(msg.mMediaUrl); return; }

    // Only fetch if it's a media type with a Meta media ID but no local URL yet
    const mediaTypes = ['image','video','audio','document','sticker'];
    if (!msg.mMediaId || !mediaTypes.includes(msg.mType) || fetching) return;

    setFetching(true);
    inboxFetchMedia(bId, msg.mChatId, msg.mId, msg.mMediaId)
      .then(r => { if (r.data?.url) setImgSrc(r.data.url); })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [msg.mId, msg.mMediaUrl, msg.mMediaId, msg.mType, bId]);

  return (
    <div
      className={`msg-bubble-wrap ${isOut ? 'out' : 'in'}`}
      data-msg-id={msg.mId}
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Reply icon revealed behind the bubble as it's swiped right */}
      {swipeX > 0 && (
        <div className="msg-swipe-reply-icon" style={{ opacity: Math.min(swipeX / 50, 1) }}>↩</div>
      )}

      <div
        className={`msg-bubble ${isAgent ? 'agent-bubble' : ''} ${isNote ? 'note-bubble' : ''}`}
        style={{
          ...(swipeX ? { transform: `translateX(${swipeX}px)` } : {}),
          ...(isNote ? { background: '#fef3c7', color: '#713f12', border: '1.5px dashed #d97706' } : {}),
          ...(highlighted ? {
            boxShadow: '0 0 0 3px #fbbf24, 0 0 0 6px rgba(251,191,36,0.35)',
            transition: 'box-shadow 0.3s ease',
          } : {}),
        }}
      >
        {isNote && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '1px 8px',
            borderRadius: 999,
            background: '#d97706',
            color: '#fff',
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            marginBottom: 4,
            boxSizing: 'border-box',
          }}>
            📝 Note{msg.agentName ? ` · ${msg.agentName}` : ''} · team only
          </div>
        )}
        {isAgent && !isNote && (
          <div className="msg-agent-label">🧑 {msg.agentName || 'Agent'}</div>
        )}

        {/* Quoted reply preview, if this message was sent as a reply */}
        {msg.mReplyToWaId && msg.replyPreviewText && (
          <div className="msg-quote-preview">
            <div className="msg-quote-bar" />
            <div className="msg-quote-text">{msg.replyPreviewText}</div>
          </div>
        )}

        {/* Text */}
        {msg.mText && <p className="msg-text">{msg.mText}</p>}

        {/* Image */}
        {msg.mType === 'image' && (
          imgSrc
            ? <img src={imgSrc} alt="img" className="msg-media-img" onClick={() => window.open(imgSrc)} />
            : <div className="msg-media-loading">📷 Loading image…</div>
        )}

        {/* Video */}
        {msg.mType === 'video' && imgSrc && (
          <video src={imgSrc} controls className="msg-media-video" />
        )}

        {/* Audio */}
        {msg.mType === 'audio' && imgSrc && (
          <audio src={imgSrc} controls className="msg-media-audio" />
        )}

        {/* Document */}
        {msg.mType === 'document' && imgSrc && (
          <a href={imgSrc} target="_blank" rel="noreferrer" className="msg-doc-link">
            📄 {msg.mMediaName || 'Download document'}
          </a>
        )}

        {/* Location */}
        {msg.mType === 'location' && msg.mLat && (
          <a
            href={`https://maps.google.com/?q=${msg.mLat},${msg.mLng}`}
            target="_blank"
            rel="noreferrer"
            className="msg-location-link"
          >
            📍 View location ({msg.mLat}, {msg.mLng})
          </a>
        )}

        {/* Sticker */}
        {msg.mType === 'sticker' && imgSrc && (
          <img src={imgSrc} alt="sticker" className="msg-sticker" />
        )}

        <span className="msg-time">
          {new Date(msg.mCreatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {/* Context menu — right-click (web) or long-press (touch). Positioned
          with position:fixed at the actual click/touch coordinates, so it
          renders relative to the viewport itself rather than any scrolled
          ancestor — it can't end up disconnected from where you clicked. */}
      {ctxMenu && (
        <div
          className="msg-ctx-menu"
          style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
        >
          {ctxMenu.mode === 'menu' && (
            <>
              {onReply && (
                <button type="button" className="msg-ctx-item" onClick={() => { onReply(); setCtxMenu(null); }}>
                  ↩ Reply
                </button>
              )}
              {onReact && (
                <button type="button" className="msg-ctx-item" onClick={() => setCtxMenu(m => ({ ...m, mode: 'emoji' }))}>
                  🙂 React
                </button>
              )}
            </>
          )}
          {ctxMenu.mode === 'emoji' && onReact && (
            <div className="msg-ctx-emoji-grid">
              {REACT_EMOJIS.map(e => (
                <button key={e} type="button" className="msg-react-emoji"
                  onClick={() => { onReact(e); setCtxMenu(null); }}>{e}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Agents management panel ───────────────────────────────────
function AgentsPanel({ bId, agents, onReload, agentModal, setAgentModal }) {
  const [form, setForm]         = useState({ name: '', email: '', password: '' });
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | agent obj

  async function handleSave() {
    if (!form.name || !form.email || (!agentModal?.aId && !form.password)) {
      toast.error('Name, email and password required'); return;
    }
    setSaving(true);
    try {
      if (agentModal?.aId) {
        await inboxUpdateAgent(bId, { agentId: agentModal.aId, ...form });
        toast.success('Agent updated');
      } else {
        await inboxCreateAgent(bId, form);
        toast.success('Agent created');
      }
      setAgentModal(null);
      setForm({ name: '', email: '', password: '' });
      onReload();
    } catch { toast.error('Failed to save agent'); }
    setSaving(false);
  }

  async function doDelete(agent) {
    try {
      await inboxDeleteAgent(bId, agent.aId);
      toast.success('Agent deleted');
      onReload();
    } catch { toast.error('Failed to delete'); }
  }

  async function handleCopyInviteLink() {
    const link = `${window.location.origin}/agent?bId=${encodeURIComponent(bId)}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied — send it with the agent\'s email and password');
    } catch {
      window.prompt('Copy this invite link:', link);
    }
  }

  return (
    <div className="agents-panel">
      <div className="agents-panel-header">
        <span className="agents-panel-header-title">Agents ({agents.length})</span>
        <div className="agents-panel-header-actions">
          <button className="btn btn-sm btn-outline" onClick={handleCopyInviteLink}>
            <i className="fas fa-link" /> Invite link
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => { setForm({ name: '', email: '', password: '' }); setAgentModal('create'); }}
          >
            <i className="fas fa-plus" /> Add Agent
          </button>
        </div>
      </div>

      <div className="inbox-empty" style={{ padding: '8px 12px', fontSize: 11.5 }}>
        Send each agent the invite link above along with the email and
        password you set for them — it pre-fills the Bot ID so they only
        need to type their email and password.
      </div>

      {agents.length === 0 ? (
        <div className="inbox-empty">No agents yet. Add one above.</div>
      ) : (
        <ul className="agent-list">
          {agents.map(a => (
            <li key={a.aId} className="agent-item">
              <div className="agent-avatar">{a.aName[0].toUpperCase()}</div>
              <div className="agent-meta">
                <div className="agent-name">{a.aName}</div>
                <div className="agent-email">{a.aEmail}</div>
              </div>
              <div className="agent-actions">
                <button className="icon-btn" onClick={() => {
                  setForm({ name: a.aName, email: a.aEmail, password: '' });
                  setAgentModal(a);
                }}>✏️</button>
                <button className="icon-btn danger" onClick={() => setConfirmDelete(a)}>🗑️</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Agent modal */}
      {agentModal && (
        <div className="modal-overlay" onClick={() => setAgentModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{agentModal?.aId ? 'Edit Agent' : 'Add Agent'}</h3>
              <button className="icon-btn" onClick={() => setAgentModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <label className="field-label">Name</label>
              <input
                className="field-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Agent name"
              />
              <label className="field-label">Email</label>
              <input
                className="field-input"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="agent@example.com"
              />
              <label className="field-label">
                Password {agentModal?.aId && <span className="text-muted">(leave blank to keep)</span>}
              </label>
              <input
                className="field-input"
                type="password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="••••••••"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setAgentModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Agent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete agent?"
          message={`"${confirmDelete.aName}" will lose access immediately and any conversations assigned to them will need to be reassigned.`}
          confirmLabel="Delete agent"
          danger
          onConfirm={() => { doDelete(confirmDelete); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ── Canned responses management panel ──────────────────────────
function CannedResponsesPanel({ bId, cannedResponses, onReload, cannedModal, setCannedModal }) {
  const [form, setForm]         = useState({ shortcut: '', body: '' });
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | canned obj

  async function handleSave() {
    const shortcut = form.shortcut.trim().replace(/^\/+/, '');
    const body     = form.body.trim();
    if (!shortcut || !body) {
      toast.error('Shortcut and reply text are required'); return;
    }
    setSaving(true);
    try {
      if (cannedModal?.crId) {
        await inboxUpdateCanned(bId, { crId: cannedModal.crId, shortcut, body });
        toast.success('Canned response updated');
      } else {
        await inboxCreateCanned(bId, { shortcut, body });
        toast.success('Canned response created');
      }
      setCannedModal(null);
      setForm({ shortcut: '', body: '' });
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save canned response');
    }
    setSaving(false);
  }

  async function doDelete(c) {
    try {
      await inboxDeleteCanned(bId, c.crId);
      toast.success('Canned response deleted');
      onReload();
    } catch { toast.error('Failed to delete'); }
  }

  return (
    <div className="agents-panel">
      <div className="agents-panel-header">
        <span className="agents-panel-header-title">Canned Responses ({cannedResponses.length})</span>
        <div className="agents-panel-header-actions">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => { setForm({ shortcut: '', body: '' }); setCannedModal('create'); }}
          >
            <i className="fas fa-plus" /> Add Shortcut
          </button>
        </div>
      </div>

      <div className="inbox-empty" style={{ padding: '8px 12px', fontSize: 11.5 }}>
        Type <strong>/</strong> followed by a shortcut in the reply box (e.g. <strong>/pricing</strong>)
        to instantly insert the saved reply below.
      </div>

      {cannedResponses.length === 0 ? (
        <div className="inbox-empty">No canned responses yet. Add one above.</div>
      ) : (
        <ul className="agent-list">
          {cannedResponses.map(c => (
            <li key={c.crId} className="agent-item">
              <div className="agent-avatar">/</div>
              <div className="agent-meta">
                <div className="agent-name">/{c.crShortcut}</div>
                <div className="agent-email">{c.crBody}</div>
              </div>
              <div className="agent-actions">
                <button className="icon-btn" onClick={() => {
                  setForm({ shortcut: c.crShortcut, body: c.crBody });
                  setCannedModal(c);
                }}>✏️</button>
                <button className="icon-btn danger" onClick={() => setConfirmDelete(c)}>🗑️</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Canned response modal */}
      {cannedModal && (
        <div className="modal-overlay" onClick={() => setCannedModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{cannedModal?.crId ? 'Edit Canned Response' : 'Add Canned Response'}</h3>
              <button className="icon-btn" onClick={() => setCannedModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <label className="field-label">Shortcut</label>
              <input
                className="field-input"
                value={form.shortcut}
                onChange={e => setForm(f => ({ ...f, shortcut: e.target.value }))}
                placeholder="pricing"
              />
              <label className="field-label">Reply text</label>
              <textarea
                className="field-input"
                rows={5}
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Our plans start at $9/month…"
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setCannedModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete canned response?"
          message={`"/${confirmDelete.crShortcut}" will no longer be available in the reply autocomplete.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => { doDelete(confirmDelete); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ── Labels management panel ─────────────────────────────────────
const LABEL_PALETTE = ['#6366f1','#ef4444','#f59e0b','#10b981','#0ea5a4','#3b82f6','#a855f7','#ec4899','#84cc16','#64748b'];

function LabelsPanel({ bId, labels, onReload, labelModal, setLabelModal }) {
  const [form, setForm]         = useState({ name: '', color: LABEL_PALETTE[0] });
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // null | label obj

  async function handleSave() {
    const name = form.name.trim();
    if (!name) { toast.error('Label name is required'); return; }
    setSaving(true);
    try {
      if (labelModal?.lId) {
        await inboxUpdateLabel(bId, { lId: labelModal.lId, name, color: form.color });
        toast.success('Label updated');
      } else {
        await inboxCreateLabel(bId, { name, color: form.color });
        toast.success('Label created');
      }
      setLabelModal(null);
      setForm({ name: '', color: LABEL_PALETTE[0] });
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save label');
    }
    setSaving(false);
  }

  async function doDelete(l) {
    try {
      await inboxDeleteLabel(bId, l.lId);
      toast.success('Label deleted');
      onReload();
    } catch { toast.error('Failed to delete'); }
  }

  return (
    <div className="agents-panel">
      <div className="agents-panel-header">
        <span className="agents-panel-header-title">Labels ({labels.length})</span>
        <div className="agents-panel-header-actions">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => { setForm({ name: '', color: LABEL_PALETTE[0] }); setLabelModal('create'); }}
          >
            <i className="fas fa-plus" /> Add Label
          </button>
        </div>
      </div>

      <div className="inbox-empty" style={{ padding: '8px 12px', fontSize: 11.5 }}>
        Labels let you triage and filter conversations at a glance — apply them from any open chat's header.
      </div>

      {labels.length === 0 ? (
        <div className="inbox-empty">No labels yet. Add one above.</div>
      ) : (
        <ul className="agent-list">
          {labels.map(l => (
            <li key={l.lId} className="agent-item">
              <div style={{
                width: 32, height: 32, minWidth: 32, borderRadius: '50%',
                background: l.lColor, color: '#fff', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                fontSize: 14, boxSizing: 'border-box',
              }}>●</div>
              <div className="agent-meta">
                <div className="agent-name">{l.lName}</div>
              </div>
              <div className="agent-actions">
                <button className="icon-btn" onClick={() => {
                  setForm({ name: l.lName, color: l.lColor });
                  setLabelModal(l);
                }}>✏️</button>
                <button className="icon-btn danger" onClick={() => setConfirmDelete(l)}>🗑️</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Label modal */}
      {labelModal && (
        <div className="modal-overlay" onClick={() => setLabelModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{labelModal?.lId ? 'Edit Label' : 'Add Label'}</h3>
              <button className="icon-btn" onClick={() => setLabelModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <label className="field-label">Name</label>
              <input
                className="field-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="billing, urgent, vip…"
              />
              <label className="field-label">Color</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {LABEL_PALETTE.map(c => (
                  <ColorSwatch
                    key={c}
                    color={c}
                    active={form.color === c}
                    onClick={() => setForm(f => ({ ...f, color: c }))}
                  />
                ))}
              </div>
              <div style={{ marginTop: 2 }}>
                <LabelChip label={{ lName: form.name.trim() || 'Preview', lColor: form.color }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setLabelModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete label?"
          message={`"${confirmDelete.lName}" will be removed from every conversation it's applied to.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => { doDelete(confirmDelete); setConfirmDelete(null); }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ── Reports panel — SLA / response-time reporting ────────────────
function formatDuration(secs) {
  if (secs === null || secs === undefined) return '—';
  secs = Math.round(secs);
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) return remMins ? `${hrs}h ${remMins}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs ? `${days}d ${remHrs}h` : `${days}d`;
}

function ReportsPanel({ data, loading, range, onRangeChange }) {
  const overview = data?.overview;
  const perAgent = data?.perAgent ?? [];

  return (
    <div className="agents-panel">
      <div style={{ padding: '10px 12px 0', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { key: 'today', label: 'Today' },
            { key: 'week',  label: 'This Week' },
            { key: 'all',   label: 'All Time' },
          ].map(r => {
            const active = range === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => onRangeChange(r.key)}
                style={{
                  flex: 1, padding: '6px 4px', borderRadius: 7, border: 'none',
                  background: active ? '#00C88C' : '#f3f4f6',
                  color: active ? '#fff' : '#6b7280',
                  fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  boxSizing: 'border-box', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="inbox-empty">Loading…</div>
      ) : !overview ? (
        <div className="inbox-empty">No report data yet.</div>
      ) : (
        <div style={{ padding: '14px 12px', overflowY: 'auto', flex: 1, minHeight: 0, boxSizing: 'border-box' }}>
          {/* Overview cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', boxSizing: 'border-box' }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Avg first response
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 3 }}>
                {formatDuration(overview.avgFirstResponseSecs)}
              </div>
            </div>
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', boxSizing: 'border-box' }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Avg resolution time
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 3 }}>
                {formatDuration(overview.avgResolutionSecs)}
              </div>
            </div>
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '12px 14px', boxSizing: 'border-box' }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Conversations resolved
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 3 }}>
                {overview.resolvedCount}
              </div>
            </div>
          </div>

          {/* Per-agent breakdown */}
          <div style={{ fontSize: 10.5, fontWeight: 700, color: '#8a94a3', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
            By agent
          </div>
          {perAgent.length === 0 ? (
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              No resolved conversations with an agent involved in this range yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {perAgent.map(a => (
                <div key={a.agentId} style={{
                  border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', boxSizing: 'border-box',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#1f2937' }}>{a.agentName || 'Unknown agent'}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 800, color: '#00966b', background: '#e8f9f3',
                      borderRadius: 999, padding: '1px 8px', boxSizing: 'border-box',
                    }}>
                      {a.resolvedCount} resolved
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 9.5, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Response</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{formatDuration(a.avgFirstResponseSecs)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9.5, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Resolution</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{formatDuration(a.avgResolutionSecs)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}